<template>
  <div class="enhanced-video-player">
    <div class="video-wrapper">
      <video
        ref="videoPlayer"
        :src="src"
        :poster="poster"
        :preload="preload"
        controlsList="nodownload"
        class="video-element"
        @loadedmetadata="onMetadataLoaded"
        @loadeddata="updateBuffered"
        @timeupdate="onTimeUpdate"
        @progress="onProgress"
        @error="onVideoError"
        :autoplay="autoplay"
        :loop="loop"
      >
        您的浏览器不支持视频播放
      </video>
      
      <!-- 自定义控制栏覆盖层 -->
      <div class="custom-controls" v-if="showControls">
        <div 
          class="progress-container" 
          @mousedown="startDrag"
          @click="seekVideo"
          ref="progressContainer"
        >
          <div class="progress-buffered" :style="{ width: bufferedPercent + '%' }"></div>
          <div 
            class="progress-bar" 
            :style="{ width: progress + '%' }"
          ></div>
          <div 
            class="progress-handle" 
            @mousedown.stop="startDrag"
            :style="{ left: progress + '%' }"
          ></div>
        </div>
        
        <div class="control-buttons">
          <button 
            class="control-btn" 
            @click="togglePlay"
            title="播放/暂停"
          >
            <el-icon v-if="!isPlaying"><VideoPlay /></el-icon>
            <el-icon v-else><VideoPause /></el-icon>
          </button>
          
          <div class="time-display">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </div>
          
          <div class="volume-control">
            <button class="control-btn" @click="toggleMute" title="静音">
              <el-icon v-if="!isMuted"><Volume /></el-icon>
              <el-icon v-else><VolumeMuted /></el-icon>
            </button>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.05" 
              :value="volume" 
              @input="setVolume"
              class="volume-slider"
            />
          </div>
          
          <div class="speed-control">
            <el-select 
              v-model="playbackSpeed" 
              @change="setPlaybackSpeed"
              size="small"
              class="speed-select"
              title="播放速度"
            >
              <el-option label="0.5x" :value="0.5" />
              <el-option label="0.75x" :value="0.75" />
              <el-option label="1x" :value="1" />
              <el-option label="1.25x" :value="1.25" />
              <el-option label="1.5x" :value="1.5" />
              <el-option label="2x" :value="2" />
            </el-select>
          </div>
          
          <button 
            class="control-btn fullscreen-btn" 
            @click="toggleFullscreen"
            title="全屏"
          >
            <el-icon v-if="!isFullscreen"><FullScreen /></el-icon>
            <el-icon v-else><CloseFullScreen /></el-icon>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 加载/缓冲指示器 -->
    <div class="loading-indicator" v-if="isLoading || (showBufferingHint && bufferedPercent < 99)">
      <el-icon class="loading-spinner"><Loading /></el-icon>
      <span>{{ isLoading ? '视频加载中...' : '缓冲中 ' + Math.round(bufferedPercent) + '%' }}</span>
    </div>
    
    <!-- 错误提示 -->
    <div class="error-message" v-if="hasError">
      <el-icon class="error-icon"><CircleClose /></el-icon>
      <p>{{ errorMessage }}</p>
      <el-button type="primary" @click="retryLoading">重试</el-button>
    </div>
  </div>
</template>

<script>
import { ElIcon, ElSelect, ElOption, ElButton } from 'element-plus'
import { 
  VideoPlay, VideoPause, Volume, VolumeMuted, 
  FullScreen, CloseFullScreen, Loading, CircleClose 
} from '@element-plus/icons-vue'

export default {
  name: 'EnhancedVideoPlayer',
  components: {
    ElIcon,
    ElSelect,
    ElOption,
    ElButton,
    VideoPlay,
    VideoPause,
    Volume,
    VolumeMuted,
    FullScreen,
    CloseFullScreen,
    Loading,
    CircleClose
  },
  props: {
    src: {
      type: String,
      required: true
    },
    poster: {
      type: String,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    initialVolume: {
      type: Number,
      default: 1.0,
      validator: (value) => value >= 0 && value <= 1
    },
    /** 预加载策略：auto=尽量缓冲（大视频推荐）, metadata=仅元数据, none=不预加载 */
    preload: {
      type: String,
      default: 'auto',
      validator: (v) => ['auto', 'metadata', 'none'].includes(v)
    }
  },
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      bufferedEnd: 0,
      volume: this.initialVolume,
      isMuted: false,
      playbackSpeed: 1.0,
      isFullscreen: false,
      showControls: true,
      isLoading: true,
      hasError: false,
      errorMessage: '',
      controlsTimeout: null,
      originalVolume: 1.0,
      isDragging: false
    }
  },
  mounted() {
    this.setupEventListeners()
    this.handleHideControlsAfterDelay()
  },
  beforeUnmount() {
    this.cleanupEventListeners()
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout)
    }
    // 清理拖拽事件监听器
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  },
  computed: {
    progress() {
      if (!this.duration || this.duration === 0) return 0
      return Math.min(100, (this.currentTime / this.duration) * 100)
    },
    bufferedPercent() {
      if (!this.duration || this.duration === 0) return 0
      return Math.min(100, (this.bufferedEnd / this.duration) * 100)
    },
    showBufferingHint() {
      return this.duration > 0 && this.currentTime < 1 && !this.hasError
    }
  },
  methods: {
    setupEventListeners() {
      const videoWrapper = this.$el.querySelector('.video-wrapper')
      if (videoWrapper) {
        videoWrapper.addEventListener('mousemove', this.handleShowControls)
        videoWrapper.addEventListener('mouseleave', this.handleHideControlsAfterDelay)
        videoWrapper.addEventListener('click', this.handleWrapperClick)
      }
      
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
    },
    
    cleanupEventListeners() {
      const videoWrapper = this.$el.querySelector('.video-wrapper')
      if (videoWrapper) {
        videoWrapper.removeEventListener('mousemove', this.handleShowControls)
        videoWrapper.removeEventListener('mouseleave', this.handleHideControlsAfterDelay)
        videoWrapper.removeEventListener('click', this.handleWrapperClick)
      }
      
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
    },
    
    onMetadataLoaded() {
      if (this.$refs.videoPlayer) {
        this.duration = this.$refs.videoPlayer.duration
        this.isPlaying = !this.$refs.videoPlayer.paused
        this.isLoading = false
      }
    },
    onProgress() {
      this.updateBuffered()
    },
    onTimeUpdate() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime
        this.isPlaying = !this.$refs.videoPlayer.paused
        this.updateBuffered()
      }
    },
    updateBuffered() {
      if (!this.$refs.videoPlayer || !this.$refs.videoPlayer.buffered.length) return
      const video = this.$refs.videoPlayer
      const current = video.currentTime
      const buffered = video.buffered
      for (let i = 0; i < buffered.length; i++) {
        if (buffered.start(i) <= current && current <= buffered.end(i)) {
          this.bufferedEnd = buffered.end(i)
          return
        }
      }
      this.bufferedEnd = buffered.end(buffered.length - 1)
    },
    
    onVideoError() {
      this.isLoading = false
      this.hasError = true
      this.errorMessage = '视频加载失败，请检查网络连接或文件是否存在'
    },
    
    retryLoading() {
      this.hasError = false
      this.isLoading = true
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.load()
      }
    },
    
    togglePlay() {
      if (this.$refs.videoPlayer) {
        if (this.$refs.videoPlayer.paused) {
          this.$refs.videoPlayer.play()
            .then(() => {
              this.isPlaying = true
            })
            .catch(error => {
              console.error('播放失败:', error)
              this.errorMessage = '无法自动播放视频，请点击播放器重试'
            })
        } else {
          this.$refs.videoPlayer.pause()
          this.isPlaying = false
        }
      }
      this.handleHideControlsAfterDelay()
    },
    
    seekVideo(event) {
      if (!this.$refs.videoPlayer || this.isDragging) return
      
      const progressContainer = event.currentTarget
      const rect = progressContainer.getBoundingClientRect()
      const pos = (event.clientX - rect.left) / rect.width
      const clampedPos = Math.max(0, Math.min(1, pos))
      this.$refs.videoPlayer.currentTime = clampedPos * this.duration
      this.handleHideControlsAfterDelay()
    },
    
    startDrag(event) {
      event.preventDefault()
      this.isDragging = true
      
      // 立即更新一次位置
      this.updateProgressFromMouse(event)
      
      // 添加全局鼠标事件监听
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    
    onDrag(event) {
      if (!this.isDragging) return
      this.updateProgressFromMouse(event)
    },
    
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      this.handleHideControlsAfterDelay()
    },
    
    updateProgressFromMouse(event) {
      if (!this.$refs.videoPlayer || !this.$refs.progressContainer) return
      
      const rect = this.$refs.progressContainer.getBoundingClientRect()
      const pos = (event.clientX - rect.left) / rect.width
      const clampedPos = Math.max(0, Math.min(1, pos))
      
      const newTime = clampedPos * this.duration
      this.$refs.videoPlayer.currentTime = newTime
      // 立即更新显示，不等视频事件
      this.currentTime = newTime
    },
    
    toggleMute() {
      if (!this.$refs.videoPlayer) return
      
      if (this.isMuted) {
        // 取消静音，恢复原来的音量
        this.$refs.videoPlayer.volume = this.originalVolume
        this.volume = this.originalVolume
      } else {
        // 静音，保存当前音量
        this.originalVolume = this.$refs.videoPlayer.volume
        this.$refs.videoPlayer.volume = 0
        this.volume = 0
      }
      this.isMuted = !this.isMuted
    },
    
    setVolume(event) {
      if (!this.$refs.videoPlayer) return
      
      const volume = parseFloat(event.target.value)
      this.$refs.videoPlayer.volume = volume
      this.volume = volume
      this.isMuted = volume === 0
      if (volume > 0) {
        this.originalVolume = volume
      }
    },
    
    setPlaybackSpeed() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.playbackRate = this.playbackSpeed
      }
    },
    
    toggleFullscreen() {
      const videoWrapper = this.$el.querySelector('.video-wrapper')
      if (!videoWrapper) return
      
      if (!document.fullscreenElement) {
        // 进入全屏
        if (videoWrapper.requestFullscreen) {
          videoWrapper.requestFullscreen()
        } else if (videoWrapper.webkitRequestFullscreen) {
          videoWrapper.webkitRequestFullscreen()
        } else if (videoWrapper.mozRequestFullScreen) {
          videoWrapper.mozRequestFullScreen()
        } else if (videoWrapper.msRequestFullscreen) {
          videoWrapper.msRequestFullscreen()
        }
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || 
                             document.webkitFullscreenElement || 
                             document.mozFullScreenElement || 
                             document.msFullscreenElement)
    },
    
    handleShowControls() {
      this.showControls = true
      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout)
      }
    },
    
    handleHideControlsAfterDelay() {
      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout)
      }
      this.controlsTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.showControls = false
        }
      }, 3000)
    },
    
    handleWrapperClick(event) {
      // 只有点击在视频区域（非控制按钮）时才切换播放状态
      if (!event.target.closest('.control-btn') && 
          !event.target.closest('.progress-container') &&
          !event.target.closest('.speed-select')) {
        this.togglePlay()
      }
    },
    
    formatTime(seconds) {
      if (isNaN(seconds)) return '00:00'
      
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.enhanced-video-player {
  position: relative;
  width: 100%;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 */
  background-color: #000;
  cursor: pointer;
}

.video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.custom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 10px;
  color: #fff;
  transition: opacity 0.3s ease;
}

.progress-container {
  position: relative;
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  margin-bottom: 10px;
  cursor: pointer;
  padding: 6px 0; /* 增加点击区域 */
  margin-top: -6px;
}

.progress-container:hover {
  height: 6px;
  margin-top: -7px;
}

.progress-buffered {
  position: absolute;
  top: 6px;
  left: 0;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.25);
  border-radius: 2px;
  pointer-events: none;
  z-index: 0;
}

.progress-container:hover .progress-buffered {
  height: 6px;
  top: 6px;
}

.progress-bar {
  position: absolute;
  top: 6px; /* 对应padding */
  left: 0;
  height: 4px;
  background-color: #409eff;
  border-radius: 2px;
  transition: width 0.05s linear;
  pointer-events: none;
  z-index: 1;
}

.progress-container:hover .progress-bar {
  height: 6px;
  top: 6px;
}

.progress-handle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin-left: -7px; /* 小球宽度的一半，使其居中对齐 */
  width: 14px;
  height: 14px;
  background-color: #409eff;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  cursor: grab;
  transition: left 0.05s ease, opacity 0.1s ease;
  opacity: 0;
  z-index: 10;
}

.progress-handle:active {
  cursor: grabbing;
}

.progress-container:hover .progress-handle {
  opacity: 1;
}

.progress-handle:hover {
  transform: translateY(-50%) scale(1.3);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
}

.control-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-btn {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.control-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.time-display {
  font-size: 14px;
  min-width: 80px;
  color: #fff;
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 5px;
}

.volume-slider {
  width: 80px;
  cursor: pointer;
}

.speed-control {
  margin-left: auto;
}

.speed-select {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.speed-select .el-input__wrapper {
  background-color: transparent;
  color: #fff;
}

.fullscreen-btn {
  margin-left: 10px;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 20px;
  border-radius: 8px;
}

.loading-spinner {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

.error-message {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 20px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  color: #f56c6c;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .control-buttons {
    gap: 5px;
  }
  
  .time-display {
    font-size: 12px;
    min-width: 70px;
  }
  
  .volume-slider {
    width: 60px;
  }
  
  .speed-control {
    display: none;
  }
}
</style>
